import React, { useEffect, useState, useMemo } from 'react';
import { CampaignStatus, LocationDetails, LocationStats, Campaign, ProductType } from '@dev/ee-api/ee-api-models';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { parse } from 'query-string';
import { useLocation } from 'react-router-dom';
import { ToggleButtonGroup, WorldMap, palette, SimpleLoader } from '@dev/ee-components';
import styled from 'styled-components';
import { isEmpty } from 'lodash-es';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { ChannelLocation } from '@dev/ee-api/ee-api-models';
import { replace as Replace } from 'redux-first-history';
import { GetCampaigns, getCampaignsStore } from '@/store/campaigns/actions';
import { GetChannels } from '@/store/channels/actions';
import dayjs from '@/services/tools/dayjs';
import {
    LONG_DATE_FORMAT,
    MAX_CAMPAIGNS_LIST_LENGTH,
    MAX_CAMPAIGNS_LIST_WITH_QUERY_LENGTH,
    MAX_CHANNELS_LIST_LENGTH,
    MAX_CHANNELS_LIST_WITH_QUERY_LENGTH,
} from '@/services/constants';

import {
    GeographyToggleBtnValues,
    GeographyToggleBtnOptInterface,
    WorldMapsEnum,
} from '@/components/Contacts/Geography/GeographyInterface';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import useMobile from '@/services/hooks/useMobile';
import { isWorldMapSelected, getMapData } from '@/components/Contacts/Geography/helpers';

import { GetReportsLocationStatsByName, getReportsState } from '@/store/reports/actions';

import {
    getActivityLocationsStore,
    StartLocationCountryLoading,
    StartLocationCountryChannelLoading,
    StartLocationsWorldLoading,
} from '@/store/activity/locations/actions';
import { getAccountStore } from '@/store/account/actions';
import TableWithCountry from '@/components/Contacts/Geography/components/TableWithCountry';
import { addParamToUrl } from '@/services/tools/handleUrlParams';
import Filters from '@/components/Contacts/Geography/components/Filters';
import AlertInfo from '@/components/Contacts/Geography/components/AlertInfo';

const GeographyOverviewStyled = styled.div`
    padding-top: 26px;
`;

const LoaderWrapper = styled.div`
    height: 515px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const MapWrapper = styled.div`
    position: relative;
    display: flex;
    width: 99%;
    height: 515px;
    overflow: hidden;
`;

const FiltersWrapper = styled.div`
    display: flex;
    justify-content: end;
`;

const ToggleButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    background-color: inherit;
    margin-bottom: 24px;
`;

const toggleBtnOptions: GeographyToggleBtnOptInterface[] = [
    {
        label: '#',
        value: GeographyToggleBtnValues.Number,
    },
    {
        label: '%',
        value: GeographyToggleBtnValues.Percent,
    },
];

const campaignStatuses = [
    CampaignStatus.Active,
    CampaignStatus.Processing,
    CampaignStatus.Sending,
    CampaignStatus.Completed,
    CampaignStatus.Paused,
    CampaignStatus.Cancelled,
];

enum DataSource {
    LOCATIONS,
    REPORTS,
}

const mapLoader = (
    <LoaderWrapper>
        <SimpleLoader color={palette.indigo[6]} />
    </LoaderWrapper>
);

const getCampaignsByQuery = (dispatch, queryCampaignName: string | undefined, isMobile: boolean): void => {
    const limit = isMobile ? 0 : !!queryCampaignName ? MAX_CAMPAIGNS_LIST_WITH_QUERY_LENGTH : MAX_CAMPAIGNS_LIST_LENGTH;
    dispatch(
        GetCampaigns({
            isUrlUpdateDisabled: true,
            search: isMobile || !queryCampaignName ? null : queryCampaignName,
            limit,
            status: campaignStatuses,
        })
    );
};

const getChannelsByQuery = (dispatch, queryChannelName, isMobile): void => {
    const limit = isMobile ? 0 : !!queryChannelName ? MAX_CHANNELS_LIST_WITH_QUERY_LENGTH : MAX_CHANNELS_LIST_LENGTH;
    dispatch(
        GetChannels({
            search: queryChannelName ?? null,
            perPage: limit,
            skipSagaURL: false,
        })
    );
};

const GeographyOverview = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const isMobile = useMobile();
    const query = parse(location.search);
    const [dataSource, setDataSource] = useState<DataSource>(DataSource.LOCATIONS);
    const locationsState = useSelector(getActivityLocationsStore);
    const { locationStats: reportsState } = useSelector(getReportsState);
    const campaignsState = useSelector(getCampaignsStore);
    const { GeneralInfo } = useSelector(getAccountStore);
    const hasCampaigns = [ProductType.Marketing, ProductType.CreatorSuite].includes(GeneralInfo?.ProductType);

    const { dataSet: locationsDataSet, loading: locationsLoading } = locationsState;
    const { dataSet: reportsDataSet, loading: reportsLoading } = reportsState;
    const data =
        dataSource === DataSource.LOCATIONS
            ? (locationsDataSet as ChannelLocation)?.LocationStats ??
              (locationsState.dataSet as LocationStats[] | LocationDetails[])
            : reportsDataSet;

    const loading = dataSource === DataSource.LOCATIONS ? locationsLoading : reportsLoading;
    const paramTo = (query?.to as string) || dayjs().format(LONG_DATE_FORMAT);
    const paramFrom = (query?.from as string) || '';
    const paramCampaignName =
        ((query?.campaignName as string) && safelyDecodeBase64Url(query?.campaignName as string)) || null;
    const paramChannelName =
        ((query?.channelName as string) && safelyDecodeBase64Url(query?.channelName as string)) || null;

    const [from, setFrom] = useState(paramFrom);
    const [to, setTo] = useState(paramTo);
    const [selectedMap, setSelectedMap] = useState<WorldMapsEnum | string>(WorldMapsEnum.World);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE_COUNT);
    const [page, setPage] = useState(1);
    const [countryStatisticsFormat, setCountryStaticticsFormat] = useState<GeographyToggleBtnValues>(
        GeographyToggleBtnValues.Number
    );

    const [queryCampaignName, setQueryCampaignName] = useState<string | undefined>(paramCampaignName);
    const [queryChannelName, setQueryChannelName] = useState<string | undefined>(paramChannelName);
    const [selectedCampaignName, setSelectedCampaignName] = useState<string | undefined>(paramCampaignName);
    const [selectedChannelName, setSelectedChannelName] = useState<string | undefined>(paramChannelName);
    const sortDescending = true;
    const campaign = useMemo(
        (): Campaign | undefined =>
            campaignsState?.collection?.find((item: Campaign): boolean => item.Name === selectedCampaignName),
        [selectedCampaignName]
    );
    useEffect(() => {
        if (hasCampaigns) {
            getCampaignsByQuery(dispatch, queryCampaignName, isMobile);
        }
    }, [queryCampaignName, isMobile, hasCampaigns]);

    useEffect(() => {
        getChannelsByQuery(dispatch, queryChannelName, isMobile);
    }, [queryChannelName, isMobile]);

    useEffect(() => {
        const isWorldMap = isWorldMapSelected(selectedMap);
        const name = selectedChannelName || selectedCampaignName;
        if (isWorldMap && !name) {
            setDataSource(DataSource.LOCATIONS);
            dispatch(
                StartLocationsWorldLoading({
                    from,
                    to,
                    sortDescending,
                    onlyWithData: true,
                })
            );
        } else if (isWorldMap && name) {
            setDataSource(DataSource.REPORTS);
            dispatch(
                GetReportsLocationStatsByName({
                    channelName: name,
                    name,
                    from,
                    to,
                })
            );
        } else if (selectedMap && name) {
            setDataSource(DataSource.LOCATIONS);
            dispatch(
                StartLocationCountryChannelLoading({
                    countryiso: selectedMap,
                    name,
                    from,
                    to,
                })
            );
        } else if (selectedMap) {
            setDataSource(DataSource.LOCATIONS);
            dispatch(
                StartLocationCountryLoading({
                    countryiso: selectedMap,
                    from,
                    to,
                })
            );
        }
        setPage(1);
        const newSearch = addParamToUrl({
            from: !isEmpty(from) && from,
            to: !isEmpty(to) && to,
            campaignName: !isEmpty(selectedCampaignName) && safelyEncodeBase64Url(selectedCampaignName),
            channelName: !isEmpty(selectedChannelName) && safelyEncodeBase64Url(selectedChannelName),
        });
        dispatch(Replace({ search: newSearch }));
    }, [from, to, selectedCampaignName, selectedChannelName, selectedMap]);

    const handleReturnToWorldMap = (): void => {
        setSelectedMap(WorldMapsEnum.World);
    };
    const handleRegionClick = (countryId): void => {
        setSelectedMap(countryId);
    };
    return (
        <GeographyOverviewStyled>
            <FiltersWrapper>
                <Filters
                    showCampaignsFilter={hasCampaigns}
                    from={from}
                    to={to}
                    setFrom={setFrom}
                    setTo={setTo}
                    selectedCampaignName={selectedCampaignName}
                    setSelectedCampaignName={setSelectedCampaignName}
                    selectedChannelName={selectedChannelName}
                    setSelectedChannelName={setSelectedChannelName}
                    queryCampaignName={queryCampaignName}
                    setQueryCampaignName={setQueryCampaignName}
                    queryChannelName={queryChannelName}
                    setQueryChannelName={setQueryChannelName}
                />
            </FiltersWrapper>
            <AlertInfo campaign={campaign} />
            {(loading && mapLoader) || (
                <MapWrapper>
                    <WorldMap
                        getCountryMap={selectedMap}
                        dataSet={getMapData(
                            data,
                            isWorldMapSelected(selectedMap),
                            t('opensoccured'),
                            t('clicksoccured')
                        )}
                        hoverColor={palette.gray[9]}
                        enableGradient
                        onRegionClick={handleRegionClick}
                        onReturnToWorldMapClick={handleReturnToWorldMap}
                        showCenterMapButton
                    />
                </MapWrapper>
            )}
            <ToggleButtonWrapper>
                <ToggleButtonGroup
                    defaultValue={GeographyToggleBtnValues.Number}
                    size="small"
                    options={toggleBtnOptions}
                    onChange={(format) => setCountryStaticticsFormat(format as GeographyToggleBtnValues)}
                />
            </ToggleButtonWrapper>
            <TableWithCountry
                loading={!!loading}
                perPage={perPage}
                page={page}
                setPerPage={setPerPage}
                setPage={setPage}
                showClicks={true}
                showOpens={true}
                totalCount={data?.length ?? 0}
                countryStatisticsFormat={countryStatisticsFormat}
                data={data}
            />
        </GeographyOverviewStyled>
    );
};

export default GeographyOverview;
